import React, { useContext, useEffect, useState } from 'react';

import { Skeleton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import OrderDetailsTable from './sections/OrderDetailsTable/OrderDetailsTable';
import OrderInfoCard from './sections/OrderInfoCard/OrderInfoCard';
import CustomEmptyPage from '../../../shared/components/CustomEmptyPage/CustomEmptyPage';
import SecondaryHero from '../../../shared/components/v3/Heros/SecondaryHero/SecondaryHero';
import { REWARDS_HISTORY_ROUTE } from '../../../shared/constants/auth';
import { CompanyContext } from '../../../shared/context/company/companyContext';
import { useIsMounted } from '../../../shared/hooks/useIsMounted';
import useQueryParams from '../../../shared/hooks/useQueryParams/useQueryParams';
import { IRewardsHistoryDetails } from '../../../shared/interfaces/rewards';
import { Rewards } from '../../../shared/services/api/rewards/rewards';
import { TRANSLATION_ORDER_HISTORY_DETAIL } from '../../../shared/translations/rewards';

import '../Rewards.scss';

interface RewardsHistoryDetailsParams {
  orderId: string;
}

const RewardsHistoryDetails = () => {
  const { orderId } = useQueryParams<RewardsHistoryDetailsParams>();
  const [orderData, setOrderData] = useState<IRewardsHistoryDetails | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [hasFilter, setHasFilter] = useState(false);

  const { company } = useContext(CompanyContext);

  const isMounted = useIsMounted();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const goToHistory = () => {
    void navigate(REWARDS_HISTORY_ROUTE);
  };

  const onSearch = async (searchValue?: string, status?: string) => {
    try {
      setError(false);
      setLoading(true);
      setOrderData(undefined);
      setHasFilter(searchValue !== undefined || status !== undefined);
      if (company.id !== '') {
        const response = await Rewards.getOrderDetails(company.id, orderId, searchValue?.toLowerCase(), status);
        if (isMounted) {
          setOrderData(response);
        }
      }
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const reloadPage = () => {
    window.location.reload();
  };

  const isOutgoings = orderData?.orderInfo.transactionType === 'outgoings';

  const getTitle = () => {
    if (loading) {
      return <Skeleton width={200} variant="rectangular" />;
    }
    if (!isOutgoings) {
      return t(TRANSLATION_ORDER_HISTORY_DETAIL.title.deposits);
    }
    if (orderData?.orderInfo.transactionDetail === 'points') {
      return t(TRANSLATION_ORDER_HISTORY_DETAIL.title.points);
    }
    return t(TRANSLATION_ORDER_HISTORY_DETAIL.title.milestones);
  };

  useEffect(() => {
    if (isMounted) {
      onSearch();
    }
  }, [company, isMounted]);

  return (
    <div className="rewards-detail-container" data-experience="new">
      <SecondaryHero
        title={t(TRANSLATION_ORDER_HISTORY_DETAIL.header.heroTitle)}
        description={getTitle()}
        color="hero-rewards"
        backAction={goToHistory}
      />
      <div className="rewards-detail-content">
        {error ? (
          <CustomEmptyPage
            title={t(TRANSLATION_ORDER_HISTORY_DETAIL.error.noData.title)}
            subtitle={t(TRANSLATION_ORDER_HISTORY_DETAIL.error.noData.description)}
            newExperience={true}
            buttonText={t(TRANSLATION_ORDER_HISTORY_DETAIL.error.noData.buttonText)}
            buttonAction={reloadPage}
          />
        ) : (
          <>
            <OrderInfoCard orderInfo={orderData?.orderInfo} loading={loading} isOutgoings={isOutgoings} />

            {isOutgoings && (
              <OrderDetailsTable
                onSearch={onSearch}
                order={orderData}
                company={company}
                loading={loading}
                hasFilter={hasFilter}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default RewardsHistoryDetails;
